import tw from 'twin.macro'
import { useEffect, useState } from 'react'

import { emailRegex } from '@/utils/strings'
import { useTranslation } from '@/utils/useTranslation'

import SubmittedImage from '@/icons/icon-sent-mail.svg'

import Link from '@/components/link-component'
import Form from '@/components/forms/form'
import TextField from './fields/text-field'
import TextArea from './fields/text-area'
import SelectField from './fields/select-field'
import RadioFieldset from './fields/radio-fieldset'

interface ConfirmationValues {
  val: string
  el?: HTMLInputElement
}

export default function ContactSupport({ helpNavInitial = undefined }) {
  const { t } = useTranslation()
  const [submitted, setSubmitted] = useState(false)
  const [email1, setEmail1] = useState<ConfirmationValues>(null)
  const [email2, setEmail2] = useState<ConfirmationValues>(null)

  const products = [
    { key: 'Password Manager', label: t('Password Manager') },
    { key: 'Secrets Manager', label: t('Secrets Manager') },
    { key: 'Passwordless.dev', label: t('Passwordless.dev') },
    { key: 'Authenticator', label: t('Authenticator') },
  ]
  const subjects = [
    { key: 'Upgrade/Change Plan', label: t('Upgrade/Change Plan') },
    { key: 'Technical Support', label: t('Technical Support') },
    { key: 'Billing Support', label: t('Billing Support') },
    { key: 'Disabled Subscription or Organization', label: t('Disabled Subscription or Organization') },
    { key: 'Other', label: t('Other') },
  ]

  useEffect(() => {
    if (email1 && email2) {
      if (email1.val === email2.val) {
        email2.el.setCustomValidity('')
      } else {
        email2.el.setCustomValidity('Emails do not match')
      }
    }
  }, [email1, email2])

  return (
    <div css={[submitted && tw`bg-gray-100`]} id="support-team">
      <div css={[submitted && tw`hidden`]}>
        <div>
          <h2 tw="text-2xl mb-2">{t('Contact Our Support Team')}</h2>
          <p tw="mb-4">{t('For technical, billing, and product questions.')}</p>
        </div>
        <div>
          <Form
            id="contact-support"
            name="Contact Support"
            submitText={t('Contact Support')}
            functionPath="https://func.bitwarden.com/api/newhelpdeskticket"
            transformFormData={(formData) => {
              const location = formData.get('selfHosting') === t('No') ? 'Cloud' : 'Self-Hosting'
              formData.set('subject', `${formData.get('subject')} - ${location} - ${formData.get('product')} `)
              return formData
            }}
            afterSubmit={() => {
              setSubmitted(true)
              document.querySelector('#support-team').scrollIntoView({
                behavior: 'smooth',
              })
            }}
          >
            <div tw="grid gap-2">
              <TextField name="name" type="text" label={t('Name')} required autoFocus />
              <TextField
                name="email"
                type="email"
                label={t('Bitwarden account email')}
                required
                pattern={emailRegex}
                onUpdate={(val, el) => setEmail1({ val, el })}
              />
              <TextField
                name="confirm"
                type="email"
                label={t('Verify account email')}
                required
                pattern={emailRegex}
                onUpdate={(val, el) => setEmail2({ val, el })}
              />
              <SelectField
                name="product"
                label={t('Product')}
                required
                initialValue={
                  {
                    'Secrets Manager': 'Secrets Manager',
                  }?.[helpNavInitial?.[0]?.title] || 'Password Manager'
                }
              >
                {products.map((product) => (
                  <option key={product.key} value={product.key}>
                    {product.label}
                  </option>
                ))}
              </SelectField>
              <RadioFieldset
                label={t('Are you self-hosting?')}
                initialValue={helpNavInitial?.[0]?.title === 'Self-hosting' ? t('Yes') : t('No')}
                fields={[t('No'), t('Yes')]}
                name="selfHosting"
                required
                tooltip={t('If you are unsure, choose no.')}
              />
              <SelectField name="subject" label={t('Subject')} required>
                {subjects.map((subject) => (
                  <option key={subject.key} value={subject.key}>
                    {subject.label}
                  </option>
                ))}
              </SelectField>
              <TextArea name="message" label={t('Message...')} required rows={4} />
            </div>
          </Form>
        </div>
      </div>
      {submitted && (
        <div tw="my-6 mx-0 bg-gray-100 rounded-4xl p-5">
          <h3 tw="text-center">{t('Thank you for your message!')}</h3>
          {email1?.val && (
            <h4 tw="text-center my-6">
              <mark tw="bg-white text-indigoBlue rounded-md py-2 px-3 border-b-4 border-b-indigoBlue">
                {email1.val}
              </mark>
            </h4>
          )}
          <p>
            {t(
              'A confirmation email has been sent. Please check your inbox or spam folder to ensure you entered the correct address!'
            )}
          </p>
          <SubmittedImage tw="fill-none" />
          <p>
            {t('While we work on your request, feel free to browse around.')}{' '}
            <Link to="/help/" className="blue-link">
              {t('Help Center')}
            </Link>{' '}
            {t('or')}{' '}
            <Link to="https://community.bitwarden.com" className="blue-link">
              {t('Community Forums')}
            </Link>
          </p>
        </div>
      )}
    </div>
  )
}
